import { useState, useEffect } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import type { DateRange } from '../types';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

type QuickRange = 'today' | '7days' | '30days' | 'all' | 'custom';

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [quickRange, setQuickRange] = useState<QuickRange>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Update custom inputs when value changes externally
  useEffect(() => {
    if (value.from) {
      setCustomFrom(format(value.from, "yyyy-MM-dd'T'HH:mm"));
    }
    if (value.to) {
      setCustomTo(format(value.to, "yyyy-MM-dd'T'HH:mm"));
    }
  }, [value]);

  const handleQuickRangeChange = (range: QuickRange) => {
    setQuickRange(range);
    
    const now = new Date();
    
    switch (range) {
      case 'today':
        onChange({
          from: startOfDay(now),
          to: endOfDay(now),
        });
        break;
      case '7days':
        onChange({
          from: startOfDay(subDays(now, 7)),
          to: endOfDay(now),
        });
        break;
      case '30days':
        onChange({
          from: startOfDay(subDays(now, 30)),
          to: endOfDay(now),
        });
        break;
      case 'all':
        onChange({ from: null, to: null });
        break;
      case 'custom':
        // Keep current values when switching to custom
        break;
    }
  };

  const handleCustomFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFrom(e.target.value);
    setQuickRange('custom');
    
    const from = e.target.value ? new Date(e.target.value) : null;
    onChange({ ...value, from });
  };

  const handleCustomToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTo(e.target.value);
    setQuickRange('custom');
    
    const to = e.target.value ? new Date(e.target.value) : null;
    onChange({ ...value, to });
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Quick range buttons */}
      <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200">
        {[
          { key: 'today', label: 'Today' },
          { key: '7days', label: '7 Days' },
          { key: '30days', label: '30 Days' },
          { key: 'all', label: 'All Time' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleQuickRangeChange(key as QuickRange)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              quickRange === key
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Custom range inputs */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">From:</label>
        <input
          type="datetime-local"
          value={customFrom}
          onChange={handleCustomFromChange}
          className="input-pill w-48"
        />
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-500">To:</label>
        <input
          type="datetime-local"
          value={customTo}
          onChange={handleCustomToChange}
          className="input-pill w-48"
        />
      </div>
    </div>
  );
}
